import {createContext, useContext, useEffect, useState} from "react";
import {DispatchGroup} from "../generated/graphql";
import {
  getSelectedDispatchStations,
  hasSelectedDispatchStations,
  useDispatchStationContext
} from "./DispatchStationProvider";
import {isEqual} from "lodash";
import {useDispatchDataContext} from "./DispatchDataProvider";

type DispatchGroupProps = {
  children: JSX.Element[] | JSX.Element;
};

export type DispatchGroupState = {
  jobGroups: DispatchGroup[];
  driverGroups: DispatchGroup[];
  isDispatchGroupLoaded: boolean;
};

const initialState: DispatchGroupState = {
  jobGroups: [],
  driverGroups: [],
  isDispatchGroupLoaded: false
};

export const DispatchGroupContext = createContext<DispatchGroupState>(initialState);

const DispatchGroupProvider = ({children}: DispatchGroupProps) => {
  const dispatchStationState = useDispatchStationContext();
  const {dispatchGroups, isDispatchDataLoaded} = useDispatchDataContext();

  const [state, setState] = useState<DispatchGroupState>({
    ...initialState
  });

  useEffect(() => {
    if (!isDispatchDataLoaded) return;
    const jobGroups: DispatchGroup[] = [];
    const driverGroups: DispatchGroup[] = [];

    const isHasSelectedStations = hasSelectedDispatchStations(dispatchStationState);
    const selectedStations = Array.from(getSelectedDispatchStations(dispatchStationState).values());

    const selectedJobGroupIds = new Set<number>();
    const selectedDriverGroupIds = new Set<number>();

    selectedStations.forEach((ds) => {
      ds.jobGroups?.forEach((jg) => selectedJobGroupIds.add(jg));
      ds.driverGroups?.forEach((dg) => selectedDriverGroupIds.add(dg));
    });

    dispatchGroups.forEach((d) => {
      const gId = Number(d.dispatchGroupId);
      if (d.dispatchType === "J") {
        if (!isHasSelectedStations || selectedJobGroupIds.has(gId)) {
          jobGroups.push(d);
        }
      } else if (d.dispatchType === "D") {
        if (!isHasSelectedStations || selectedDriverGroupIds.has(gId)) {
          driverGroups.push(d);
        }
      }
    });

    if (!isEqual(state.jobGroups, jobGroups) || !isEqual(state.driverGroups, driverGroups)) {
      setState({
        jobGroups: jobGroups,
        driverGroups: driverGroups,
        isDispatchGroupLoaded: true
      });
    }
  }, [dispatchGroups, dispatchStationState, isDispatchDataLoaded, state.driverGroups, state.jobGroups]);

  return <DispatchGroupContext.Provider value={state}>{children}</DispatchGroupContext.Provider>;
};

export const useDispatchGroupContext = () => {
  const context = useContext(DispatchGroupContext);
  if (!context) {
    throw new Error("useDispatchGroupContext must be used within a DispatchGroupProvider");
  }
  return context;
};

export default DispatchGroupProvider;
